import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, Search } from 'lucide-react';
import type { MicrosoftLicence } from '../microsoftLicencePricing';
import {
  findMicrosoftLicence,
  searchMicrosoftLicences,
  suggestMicrosoftLicences,
} from '../microsoftLicenceLink';
import { useMicrosoftLicenceReference } from '../useMicrosoftLicenceReference';

const MAX_RESULTS = 50;

export interface LinkedProductRef {
  id: string;
  name: string;
  microsoftSkuId?: string;
}

interface MicrosoftLicenceFieldProps {
  /** The product being edited. Absent while a new product is being created. */
  productId?: string;
  productName: string;
  vendor?: string;
  /** The linked licence's Graph `skuId`. */
  value?: string;
  onChange: (skuId: string | undefined) => void;
  /** Called with the licence the partner chose, so the product can take its name, image and price. */
  onPick?: (licence: MicrosoftLicence) => void;
  /** The partner's products, to warn when another one is linked to the same licence. */
  otherProducts?: readonly LinkedProductRef[];
}

const sameSku = (a: string | undefined, b: string | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Links a Microsoft plan to the Microsoft licence it sells. The customer app matches a
 * tenant's licences (from Microsoft Graph) to catalogue products on this link alone, which
 * is what lets an admin buy an extra seat from the New User form.
 */
export function MicrosoftLicenceField({
  productId,
  productName,
  vendor,
  value,
  onChange,
  onPick,
  otherProducts = [],
}: MicrosoftLicenceFieldProps) {
  const { reference, error: referenceError, reload } = useMicrosoftLicenceReference();
  const licences = useMemo(() => reference?.licences ?? [], [reference]);
  const linked = findMicrosoftLicence(licences, value);
  const [searching, setSearching] = useState(!value);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const suggestions = useMemo(
    () => (value ? [] : suggestMicrosoftLicences(licences, productName, vendor)),
    [value, licences, productName, vendor],
  );
  const matches = useMemo(() => (query.trim() ? searchMicrosoftLicences(licences, query) : []), [licences, query]);
  const browse = useMemo(() => licences.filter((licence) => licence.kind === 'seat'), [licences]);
  const listed = query.trim() ? matches : browse;
  const results = listed.slice(0, MAX_RESULTS);

  const alsoLinked = value
    ? otherProducts.filter((p) => p.id !== productId && sameSku(p.microsoftSkuId, value))
    : [];

  useEffect(() => setActiveIndex(0), [query]);

  useEffect(() => {
    const item = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const choose = (licence: MicrosoftLicence) => {
    onChange(licence.skuId);
    onPick?.(licence);
    setQuery('');
    setSearching(false);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      choose(results[activeIndex]);
    }
  };

  return (
    <section className="space-y-2">
      <div>
        <label className="block text-xs font-medium text-slate-700">Microsoft licence</label>
        <p className="text-[11px] text-slate-500 mt-0.5 mb-0">
          The licence this plan sells. Customers&apos; Microsoft 365 licences are matched to it, so
          they can buy an extra seat from the New User form.
        </p>
      </div>

      {referenceError && (
        <div className="flex items-start gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
          <span className="flex-1">{referenceError}. The link can&apos;t be checked or changed until it loads.</span>
          <button
            type="button"
            onClick={reload}
            className="px-2 py-0.5 rounded-md border border-rose-300 bg-white text-xs font-medium text-rose-700 hover:bg-rose-100 transition-colors"
          >
            Try again
          </button>
        </div>
      )}
      {!reference && !referenceError && (
        <p className="m-0 text-xs text-slate-500">Loading Microsoft&apos;s licence list…</p>
      )}
      {!reference && value && (
        <p className="m-0 text-[11px] text-slate-500 font-mono break-all">Linked to {value}</p>
      )}

      {reference && value && !searching && (
        <div
          className={`flex items-start justify-between gap-3 rounded-md border px-3 py-2 ${
            linked ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <div className="flex items-start gap-2 min-w-0">
            {linked ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            ) : (
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900">
                {linked ? linked.name : "Linked to a SKU that isn't in Microsoft's licensing reference"}
              </div>
              <div className="text-[11px] text-slate-500 font-mono break-all">
                {linked ? `${linked.skuPartNumber} · ${linked.skuId}` : value}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={() => setSearching(true)}
              className="px-2 py-1 rounded-md text-xs font-medium text-slate-700 hover:bg-white transition-colors"
            >
              Change
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(undefined);
                setSearching(true);
              }}
              className="px-2 py-1 rounded-md text-xs font-medium text-rose-600 hover:bg-white transition-colors"
            >
              Remove
            </button>
          </div>
        </div>
      )}

      {linked?.kind === 'seat' && !searching && (
        <p className="m-0 flex items-start gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
          <span>
            {linked.name} is built into every customer&apos;s catalogue. Once you price it under Marketplace, Microsoft
            licences (or for one customer on their marketplace page), the built-in licence replaces this product for
            that customer.
          </span>
        </p>
      )}

      {alsoLinked.length > 0 && !searching && (
        <p className="m-0 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-px" aria-hidden="true" />
          <span>
            Also linked to {alsoLinked.map((p) => `“${p.name}”`).join(', ')}. A customer whose catalogue
            has both can&apos;t buy this licence from the New User form.
          </span>
        </p>
      )}

      {reference && searching && (
        <div className="space-y-2">
          {!value && (
            <p className="m-0 text-xs text-slate-600">
              Choose the licence. Its name, image and description are filled in, and so is the price from your distributor list.
            </p>
          )}

          {suggestions.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] font-medium text-slate-600">Matches this product&apos;s name</div>
              {suggestions.map((licence) => (
                <button
                  key={licence.skuId}
                  type="button"
                  onClick={() => choose(licence)}
                  className="w-full flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-left hover:border-orange-300 hover:bg-orange-50 transition-colors"
                >
                  <span className="text-sm text-slate-900">{licence.name}</span>
                  <span className="text-[11px] font-mono text-slate-500 flex-shrink-0">{licence.skuPartNumber}</span>
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" aria-hidden="true" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label="Search Microsoft licences"
              placeholder="Search by name, String ID (e.g. SPB) or GUID"
              className="w-full rounded-md border border-slate-300 pl-8 pr-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          <div className="rounded-md border border-slate-200 bg-white">
            {results.length === 0 ? (
              <p className="m-0 px-3 py-2 text-xs text-slate-500">No Microsoft licence matches “{query.trim()}”.</p>
            ) : (
              <ul ref={listRef} role="listbox" aria-label="Microsoft licences" className="m-0 p-1 max-h-56 overflow-auto list-none">
                {results.map((licence, index) => (
                  <li key={licence.skuId} role="option" aria-selected={index === activeIndex}>
                    <button
                      type="button"
                      onClick={() => choose(licence)}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={`w-full flex items-center justify-between gap-3 rounded px-2 py-1.5 text-left transition-colors ${
                        index === activeIndex ? 'bg-orange-50' : 'bg-transparent'
                      }`}
                    >
                      <span className="text-sm text-slate-900">{licence.name}</span>
                      <span className="text-[11px] font-mono text-slate-500 flex-shrink-0">{licence.skuPartNumber}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {listed.length > MAX_RESULTS && (
              <p className="m-0 border-t border-slate-100 px-3 py-1.5 text-[11px] text-slate-500">
                Showing {MAX_RESULTS} of {listed.length}. Keep typing to narrow it down.
              </p>
            )}
          </div>

          {value && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setSearching(false);
              }}
              className="px-2 py-1 rounded-md text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      )}
    </section>
  );
}
